import { Component, useState, type ReactNode } from 'react';
import { BRAND_MARK } from './app/brand.js';

/**
 * Kök hata görünümü. Çökmüş olabilecek hiçbir şeye DAYANMAZ: kabuk, prototip katmanları,
 * simge seti ya da tembel bir parça yoktur. Görsel dil giriş kartınınkidir (styles-v6.css
 * `.lg-card`, `.lg-logo`, `.btn.primary`) ama değerler burada SABİT yazılır — CSS katmanları
 * yüklenemediyse bile sayfa aynı görünür. Yazı tipi `layers.css` ile giriş paketindedir;
 * gelmezse sistem yazı tipine düşer.
 */
const T = {
  bg: '#F3F5F9',
  card: '#FFFFFF',
  line: '#E4E8F0',
  fg: '#0F172A',
  fg2: '#64748B',
  fg3: '#94A3B8',
  accent: '#1A1B1D',
  danger: '#DC4C5B',
  dangerSoft: '#FBE1E4',
  font: '"Plus Jakarta Sans", -apple-system, "Segoe UI", system-ui, sans-serif',
};

/**
 * Durum bildiren etkileşimler (üzerine gelme, basma, klavye odağı) satır içi stille
 * yazılamaz; sınıflar `boot-err-` önekiyle yalnız bu görünümde yaşar. Giriş animasyonu
 * süslemeseldir ve hareket azaltma tercihinde kalkar.
 */
const CSS = `
@keyframes boot-err-in { from { opacity: 0; transform: translateY(8px) scale(.985); } to { opacity: 1; transform: none; } }
.boot-err-card { animation: boot-err-in .22s cubic-bezier(.2,.8,.2,1) both; }
.boot-err-btn { transition: transform .12s cubic-bezier(.2,.8,.2,1), background-color .18s, border-color .18s, box-shadow .18s, color .18s; }
.boot-err-btn:active { transform: scale(.98); }
.boot-err-btn:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
/* Taban renkler satır içidir; durum kuralı onları ancak !important ile ezebilir. */
.boot-err-primary:hover { background-color: #000 !important; box-shadow: 0 6px 18px rgba(26,27,29,.28), 0 1px 2px rgba(15,23,42,.08) !important; }
.boot-err-secondary:hover { background-color: #F4F6FA !important; border-color: #CBD3E0 !important; }
@media (prefers-reduced-motion: reduce) {
  .boot-err-card { animation: none; }
  .boot-err-btn { transition: none; }
  .boot-err-btn:active { transform: none; }
}
`;

/** Tam sayfa gezinme: çökmüş React ağacına ya da istemci yönlendiricisine güvenilmez. */
export const bootErrorActions = {
  reload: () => window.location.reload(),
  /** Kök adres oturum varsa Bugün'e, yoksa girişe çözülür (routes.ts). */
  home: () => window.location.assign('/'),
};

const button = {
  width: '100%',
  height: 44,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '0 16px',
  borderRadius: 10,
  font: `600 13px/1 ${T.font}`,
  cursor: 'pointer',
} as const;

/** Marka işareti; görsel inmezse (ağ düştüyse olası) aynı ölçüde "D" monogramına düşer. */
function BrandMark() {
  const [failed, setFailed] = useState(false);
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width: 96,
        height: 96,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        background: 'linear-gradient(180deg, rgba(148,163,184,.12), rgba(148,163,184,0))',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: T.card,
          boxShadow: `0 1px 2px rgba(15,23,42,.08), inset 0 0 0 1px ${T.line}`,
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
        }}
      >
        {failed ? (
          <span
            data-testid="boot-error-monogram"
            style={{
              width: 36,
              height: 36,
              borderRadius: 9,
              background: T.accent,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              font: `700 17px/1 ${T.font}`,
            }}
          >
            D
          </span>
        ) : (
          <img
            src={BRAND_MARK}
            alt=""
            width={64}
            height={64}
            onError={() => setFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          />
        )}
      </div>
      {/* Durum rozeti: logonun sağ altında, hatayı renkle ve şekille bildirir. */}
      <span
        style={{
          position: 'absolute',
          right: 14,
          bottom: 14,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: T.dangerSoft,
          color: T.danger,
          boxShadow: `0 0 0 3px ${T.card}`,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.8"
          strokeLinecap="round"
        >
          <path d="M12 7v6M12 17h.01" />
        </svg>
      </span>
    </div>
  );
}

/**
 * `public`: hastanın açtığı kabuksuz sayfalar (/randevu, /odeme/…). Hastayı klinik girişine
 * götüren "Ana sayfaya dön" ve "klinik yöneticinize" metni onlara anlamsızdır.
 */
export type BootErrorAudience = 'clinic' | 'public';

export function BootErrorView({ audience = 'clinic' }: { audience?: BootErrorAudience }) {
  return (
    <main
      role="alert"
      aria-labelledby="boot-error-title"
      style={{
        minHeight: '100dvh',
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
        background: T.bg,
        color: T.fg,
        fontFamily: T.font,
        textAlign: 'center',
      }}
    >
      <style>{CSS}</style>
      {/* Giriş ekranının ızgarası (.lg-grid): ortada belirgin, kenarlara doğru söner. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${T.line} 1px, transparent 1px), linear-gradient(90deg, ${T.line} 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          backgroundPosition: 'center center',
          opacity: 0.5,
          maskImage: 'radial-gradient(60% 60%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(60% 60%, #000 30%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
      <div
        className="boot-err-card"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 440,
          boxSizing: 'border-box',
          background: T.card,
          border: `1px solid ${T.line}`,
          borderRadius: 24,
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 22,
          boxShadow: '0 30px 80px -20px rgba(15,23,42,.18), 0 0 0 1px rgba(15,23,42,.03)',
        }}
      >
        <BrandMark />
        <div>
          <h1
            id="boot-error-title"
            style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}
          >
            Uygulama açılamadı
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: T.fg2 }}>
            Sayfa yüklenirken bir sorun oluştu. İnternet bağlantınızı kontrol edip yeniden
            yükleyebilirsiniz. Kayıtlı verileriniz etkilenmez.
          </p>
        </div>
        <div style={{ alignSelf: 'stretch', height: 1, background: T.line }} />
        <div style={{ alignSelf: 'stretch', display: 'grid', gap: 10 }}>
          <button
            type="button"
            className="boot-err-btn boot-err-primary"
            onClick={() => bootErrorActions.reload()}
            style={{
              ...button,
              border: 0,
              background: T.accent,
              color: '#fff',
              boxShadow: '0 4px 14px rgba(26,27,29,.22), 0 1px 2px rgba(15,23,42,.08)',
            }}
          >
            Yeniden yükle
          </button>
          {audience === 'clinic' && (
            <button
              type="button"
              className="boot-err-btn boot-err-secondary"
              onClick={() => bootErrorActions.home()}
              style={{ ...button, border: `1px solid ${T.line}`, background: T.card, color: T.fg }}
            >
              Ana sayfaya dön
            </button>
          )}
        </div>
        <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: T.fg3 }}>
          {audience === 'clinic'
            ? 'Sorun sürerse klinik yöneticinize haber verin.'
            : 'Sorun sürerse kliniği arayarak bilgi alabilirsiniz.'}
        </p>
      </div>
    </main>
  );
}

export class BootErrorBoundary extends Component<
  { children: ReactNode; audience?: BootErrorAudience },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return <BootErrorView audience={this.props.audience ?? 'clinic'} />;
  }
}
